"use client";

import { PromotionalEventType } from "@faire/web-api--source/indigofair/common/PromotionalEventType";
import Color from "@faire/web/slate/Color";
import { Row } from "@faire/web/slate/Layout";
import {
  EventsThemeShape,
  IEventShapeProps,
} from "@faire/web/slate/Theme/EventsThemeShape";
import React from "react";
import styled, { css } from "styled-components";

interface IEventThemeShapeCombinationProps {
  frontShape: {
    component: React.ElementType<IEventShapeProps>;
    height: string;
    color?: Color;
    right?: number;
    bottom?: number;
  };
  backShape: {
    component: React.ElementType<IEventShapeProps>;
    height: string;
    color?: Color;
    right?: number;
  };
  height: string;
  width: string;
  className?: string;
}

const EventThemeCombinationBase: React.FC<IEventThemeShapeCombinationProps> = ({
  frontShape,
  backShape,
  height,
  width,
  className,
}) => {
  const FrontShape = frontShape.component;
  const BackShape = backShape.component;

  return (
    <Container height={height} width={width} className={className}>
      <ShapeWrapper $right={backShape.right}>
        <BackShape
          height={backShape.height}
          color={backShape.color}
          style={{ display: "block" }}
        />
      </ShapeWrapper>
      <ShapeWrapper $right={frontShape.right} $bottom={frontShape.bottom}>
        <FrontShape
          height={frontShape.height ?? height}
          color={frontShape.color}
          style={{ display: "block" }}
        />
      </ShapeWrapper>
    </Container>
  );
};

type eventKeys =
  | "faireMarket"
  | Extract<
      PromotionalEventType,
      | PromotionalEventType.FAIRE_WINTER_MARKET_2023
      | PromotionalEventType.FAIRE_SUMMER_MARKET_2022
    >;
type combinationKeys = "comboA" | "comboB" | "comboC";

export interface IEventShapeCombinationProps {
  height?: string;
  width?: string;
  frontShapeColor: Color;
  backShapeColor: Color;
  className?: string;
}

const ComboAShapeCombination: React.FC<IEventShapeCombinationProps> = ({
  height,
  width,
  frontShapeColor,
  backShapeColor,
  className,
}) => {
  const defaultWidth = 375;
  const defaultHeight = 193;
  const defaultFrontShapeHeight = 150;
  const defaultBackShapeHeight = 175;
  const heightPx =
    !height || isNaN(parseInt(height)) ? defaultHeight : parseInt(height);
  const widthPx =
    !width || isNaN(parseInt(width)) ? defaultWidth : parseInt(width);

  return (
    <EventThemeCombinationBase
      frontShape={{
        component: EventsThemeShape.faireMarket.tertiary,
        height: `${(defaultFrontShapeHeight / defaultHeight) * heightPx}px`,
        color: frontShapeColor,
        right: widthPx / 2.193,
      }}
      backShape={{
        component: EventsThemeShape.faireMarket.secondary,
        height: `${(defaultBackShapeHeight / defaultHeight) * heightPx}px`,
        color: backShapeColor,
        right: widthPx / 6.818,
      }}
      height={height ?? `${defaultHeight}px`}
      width={width ?? `${defaultWidth}px`}
      className={className}
    />
  );
};
const ComboAShapeCombinationFwm23: React.FC<IEventShapeCombinationProps> = ({
  height,
  width,
  frontShapeColor,
  backShapeColor,
  className,
}) => {
  const defaultWidth = 375;
  const defaultHeight = 193;
  const defaultFrontShapeHeight = 150;
  const defaultBackShapeHeight = 175;
  const heightPx =
    !height || isNaN(parseInt(height)) ? defaultHeight : parseInt(height);
  const widthPx =
    !width || isNaN(parseInt(width)) ? defaultWidth : parseInt(width);

  return (
    <EventThemeCombinationBase
      frontShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_WINTER_MARKET_2023]
            .primary,
        height: `${(defaultFrontShapeHeight / defaultHeight) * heightPx}px`,
        color: frontShapeColor,
        right: widthPx / 5,
      }}
      backShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_WINTER_MARKET_2023]
            .secondary,
        height: `${(defaultBackShapeHeight / defaultHeight) * heightPx}px`,
        color: backShapeColor,
      }}
      height={height ?? `${defaultHeight}px`}
      width={width ?? `${defaultWidth}px`}
      className={className}
    />
  );
};
const ComboAShapeCombinationFsm22: React.FC<IEventShapeCombinationProps> = ({
  height,
  width,
  frontShapeColor,
  backShapeColor,
  className,
}) => {
  const defaultWidth = 245;
  const defaultHeight = 130;
  const defaultFrontShapeHeight = 130;
  const defaultBackShapeHeight = 77;
  const heightPx =
    !height || isNaN(parseInt(height)) ? defaultHeight : parseInt(height);
  const widthPx =
    !width || isNaN(parseInt(width)) ? defaultWidth : parseInt(width);

  return (
    <EventThemeCombinationBase
      frontShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_SUMMER_MARKET_2022]
            .primary,
        height: `${(defaultFrontShapeHeight / defaultHeight) * heightPx}px`,
        color: frontShapeColor,
      }}
      backShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_SUMMER_MARKET_2022]
            .secondary,
        height: `${(defaultBackShapeHeight / defaultHeight) * heightPx}px`,
        color: backShapeColor,
        right: widthPx / 3,
      }}
      height={height ?? `${defaultHeight}px`}
      width={width ?? `${defaultWidth}px`}
      className={className}
    />
  );
};

const ComboBShapeCombination: React.FC<IEventShapeCombinationProps> = ({
  height,
  width,
  frontShapeColor,
  backShapeColor,
  className,
}) => {
  const defaultWidth = 308;
  const defaultHeight = 194;
  const defaultFrontShapeHeight = 119;
  const defaultBackShapeHeight = 194;
  const heightPx =
    !height || isNaN(parseInt(height)) ? defaultHeight : parseInt(height);
  const widthPx =
    !width || isNaN(parseInt(width)) ? defaultWidth : parseInt(width);
  return (
    <EventThemeCombinationBase
      frontShape={{
        component: EventsThemeShape.faireMarket.secondary,
        height: `${(defaultFrontShapeHeight / defaultHeight) * heightPx}px`,
        color: frontShapeColor,
        right: widthPx / 2.962,
        bottom: heightPx / 6.69,
      }}
      backShape={{
        component: EventsThemeShape.faireMarket.tertiary,
        height: `${(defaultBackShapeHeight / defaultHeight) * heightPx}px`,
        color: backShapeColor,
      }}
      height={height ?? `${defaultHeight}px`}
      width={width ?? `${defaultWidth}px`}
      className={className}
    />
  );
};

const ComboBShapeCombinationFwm23: React.FC<IEventShapeCombinationProps> = ({
  height,
  width,
  frontShapeColor,
  backShapeColor,
  className,
}) => {
  const defaultWidth = 308;
  const defaultHeight = 194;
  const defaultFrontShapeHeight = 175;
  const defaultBackShapeHeight = 194;
  const heightPx =
    !height || isNaN(parseInt(height)) ? defaultHeight : parseInt(height);
  return (
    <EventThemeCombinationBase
      frontShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_WINTER_MARKET_2023]
            .secondary,
        height: `${(defaultFrontShapeHeight / defaultHeight) * heightPx}px`,
        color: frontShapeColor,
      }}
      backShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_WINTER_MARKET_2023]
            .tertiary,
        height: `${(defaultBackShapeHeight / defaultHeight) * heightPx}px`,
        color: backShapeColor,
      }}
      height={height ?? `${defaultHeight}px`}
      width={width ?? `${defaultWidth}px`}
      className={className}
    />
  );
};

const ComboBShapeCombinationFsm22: React.FC<IEventShapeCombinationProps> = ({
  height,
  width,
  frontShapeColor,
  backShapeColor,
  className,
}) => {
  const defaultWidth = 308;
  const defaultHeight = 194;
  const defaultFrontShapeHeight = 175;
  const defaultBackShapeHeight = 194;
  const heightPx =
    !height || isNaN(parseInt(height)) ? defaultHeight : parseInt(height);
  return (
    <EventThemeCombinationBase
      frontShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_SUMMER_MARKET_2022]
            .secondary,
        height: `${(defaultFrontShapeHeight / defaultHeight) * heightPx}px`,
        color: frontShapeColor,
      }}
      backShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_SUMMER_MARKET_2022]
            .tertiary,
        height: `${(defaultBackShapeHeight / defaultHeight) * heightPx}px`,
        color: backShapeColor,
      }}
      height={height ?? `${defaultHeight}px`}
      width={width ?? `${defaultWidth}px`}
      className={className}
    />
  );
};

const ComboCShapeCombination: React.FC<IEventShapeCombinationProps> = ({
  height,
  width,
  frontShapeColor,
  backShapeColor,
  className,
}) => {
  const defaultWidth = 308;
  const defaultHeight = 194;
  const defaultFrontShapeHeight = 150;
  const defaultBackShapeHeight = 194;
  const heightPx =
    !height || isNaN(parseInt(height)) ? defaultHeight : parseInt(height);
  const widthPx =
    !width || isNaN(parseInt(width)) ? defaultWidth : parseInt(width);
  return (
    <EventThemeCombinationBase
      frontShape={{
        component: EventsThemeShape.faireMarket.primary,
        height: `${(defaultFrontShapeHeight / defaultHeight) * heightPx}px`,
        color: frontShapeColor,
        right: widthPx / 4.529,
      }}
      backShape={{
        component: EventsThemeShape.faireMarket.tertiary,
        height: `${(defaultBackShapeHeight / defaultHeight) * heightPx}px`,
        color: backShapeColor,
      }}
      height={height ?? `${defaultHeight}px`}
      width={width ?? `${defaultWidth}px`}
      className={className}
    />
  );
};
const ComboCShapeCombinationFwm23: React.FC<IEventShapeCombinationProps> = ({
  height,
  width,
  frontShapeColor,
  backShapeColor,
  className,
}) => {
  const defaultWidth = 308;
  const defaultHeight = 194;
  const defaultFrontShapeHeight = 150;
  const defaultBackShapeHeight = 194;
  const heightPx =
    !height || isNaN(parseInt(height)) ? defaultHeight : parseInt(height);

  return (
    <EventThemeCombinationBase
      frontShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_WINTER_MARKET_2023]
            .primary,
        height: `${(defaultFrontShapeHeight / defaultHeight) * heightPx}px`,
        color: frontShapeColor,
      }}
      backShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_WINTER_MARKET_2023]
            .tertiary,
        height: `${(defaultBackShapeHeight / defaultHeight) * heightPx}px`,
        color: backShapeColor,
      }}
      height={height ?? `${defaultHeight}px`}
      width={width ?? `${defaultWidth}px`}
      className={className}
    />
  );
};
const ComboCShapeCombinationFsm22: React.FC<IEventShapeCombinationProps> = ({
  height,
  width,
  frontShapeColor,
  backShapeColor,
  className,
}) => {
  const defaultWidth = 308;
  const defaultHeight = 194;
  const defaultFrontShapeHeight = 150;
  const defaultBackShapeHeight = 194;
  const heightPx =
    !height || isNaN(parseInt(height)) ? defaultHeight : parseInt(height);

  return (
    <EventThemeCombinationBase
      frontShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_SUMMER_MARKET_2022]
            .primary,
        height: `${(defaultFrontShapeHeight / defaultHeight) * heightPx}px`,
        color: frontShapeColor,
      }}
      backShape={{
        component:
          EventsThemeShape[PromotionalEventType.FAIRE_SUMMER_MARKET_2022]
            .tertiary,
        height: `${(defaultBackShapeHeight / defaultHeight) * heightPx}px`,
        color: backShapeColor,
      }}
      height={height ?? `${defaultHeight}px`}
      width={width ?? `${defaultWidth}px`}
      className={className}
    />
  );
};

export const EventsThemeShapeCombination: {
  [event in eventKeys]: {
    [combo in combinationKeys]: React.FC<IEventShapeCombinationProps>;
  };
} = {
  faireMarket: {
    comboA: ComboAShapeCombination,
    comboB: ComboBShapeCombination,
    comboC: ComboCShapeCombination,
  },
  FAIRE_WINTER_MARKET_2023: {
    comboA: ComboAShapeCombinationFwm23,
    comboB: ComboBShapeCombinationFwm23,
    comboC: ComboCShapeCombinationFwm23,
  },
  FAIRE_SUMMER_MARKET_2022: {
    comboA: ComboAShapeCombinationFsm22,
    comboB: ComboBShapeCombinationFsm22,
    comboC: ComboCShapeCombinationFsm22,
  },
};

const Container = styled(Row)`
  ${(props) => css`
    height: ${props.height};
    width: ${props.width};
  `}
  position: relative;
`;

const ShapeWrapper = styled.div<{ $right?: number; $bottom?: number }>`
  position: absolute;
  right: ${({ $right }) => $right ?? 0}px;
  bottom: ${({ $bottom }) => $bottom ?? 0}px;
`;
